'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AppContentCurrentEntity, AppContentVersionEntity } from '../../domain/entities/app-content.entity';
import { handleError } from '@/core/errors/handle-error';
import type { UpdateLegalDocumentDto } from '../../domain/dtos/update-legal-document.dto';
import type { GetTermsOfUseUseCase } from '../../domain/usecases/get-terms-of-use.usecase';
import type { GetPrivacyPolicyUseCase } from '../../domain/usecases/get-privacy-policy.usecase';
import type { GetTermsOfUseVersionsUseCase } from '../../domain/usecases/get-terms-of-use-versions.usecase';
import type { GetPrivacyPolicyVersionsUseCase } from '../../domain/usecases/get-privacy-policy-versions.usecase';
import type { UpdateTermsOfUseUseCase } from '../../domain/usecases/update-terms-of-use.usecase';
import type { UpdatePrivacyPolicyUseCase } from '../../domain/usecases/update-privacy-policy.usecase';
import type { InvalidateAppContentCacheUseCase } from '../../domain/usecases/invalidate-app-content-cache.usecase';

export interface LegalDocumentSlotState {
  current: AppContentCurrentEntity | null;
  versions: AppContentVersionEntity[];
  loading: boolean;
  error: string | null;
}

export interface AppContentState {
  terms: LegalDocumentSlotState;
  privacy: LegalDocumentSlotState;
}

export interface AppContentOperationResult {
  success: boolean;
  error: string | null;
}

export interface LegalDocumentsSnapshot {
  terms: AppContentCurrentEntity | null;
  privacy: AppContentCurrentEntity | null;
}

export interface AppContentEvents {
  refreshTerms: () => Promise<void>;
  refreshPrivacy: () => Promise<void>;
  refreshLegalDocuments: () => Promise<void>;
  /** Leitura direta (útil antes de gravar aceite no onboarding, sem depender do `setState` assíncrono). */
  fetchLegalSnapshots: () => Promise<LegalDocumentsSnapshot>;
  updateTerms: (dto: UpdateLegalDocumentDto) => Promise<AppContentOperationResult>;
  updatePrivacy: (dto: UpdateLegalDocumentDto) => Promise<AppContentOperationResult>;
  clearErrorTerms: () => void;
  clearErrorPrivacy: () => void;
}

export interface AppContentContextValue {
  state: AppContentState;
  events: AppContentEvents;
}

const emptySlot = (): LegalDocumentSlotState => ({
  current: null,
  versions: [],
  loading: false,
  error: null,
});

const initialState: AppContentState = {
  terms: emptySlot(),
  privacy: emptySlot(),
};

const AppContentContext = createContext<AppContentContextValue | null>(null);

export interface AppContentProviderProps {
  children: React.ReactNode;
  getTermsOfUseUseCase: GetTermsOfUseUseCase;
  getPrivacyPolicyUseCase: GetPrivacyPolicyUseCase;
  getTermsOfUseVersionsUseCase: GetTermsOfUseVersionsUseCase;
  getPrivacyPolicyVersionsUseCase: GetPrivacyPolicyVersionsUseCase;
  updateTermsOfUseUseCase: UpdateTermsOfUseUseCase;
  updatePrivacyPolicyUseCase: UpdatePrivacyPolicyUseCase;
  invalidateAppContentCacheUseCase: InvalidateAppContentCacheUseCase;
}

export function AppContentProvider({
  children,
  getTermsOfUseUseCase,
  getPrivacyPolicyUseCase,
  getTermsOfUseVersionsUseCase,
  getPrivacyPolicyVersionsUseCase,
  updateTermsOfUseUseCase,
  updatePrivacyPolicyUseCase,
  invalidateAppContentCacheUseCase,
}: AppContentProviderProps) {
  const [state, setState] = useState<AppContentState>(initialState);

  const refreshTerms = useCallback(async () => {
    setState((s) => ({
      ...s,
      terms: { ...s.terms, loading: true, error: null },
    }));
    try {
      const [current, versions] = await Promise.all([
        getTermsOfUseUseCase.execute(),
        getTermsOfUseVersionsUseCase.execute(),
      ]);
      setState((s) => ({
        ...s,
        terms: { current, versions, loading: false, error: null },
      }));
    } catch (e) {
      const msg = handleError(e).message;
      setState((s) => ({
        ...s,
        terms: { ...s.terms, loading: false, error: msg },
      }));
    }
  }, [getTermsOfUseUseCase, getTermsOfUseVersionsUseCase]);

  const refreshPrivacy = useCallback(async () => {
    setState((s) => ({
      ...s,
      privacy: { ...s.privacy, loading: true, error: null },
    }));
    try {
      const [current, versions] = await Promise.all([
        getPrivacyPolicyUseCase.execute(),
        getPrivacyPolicyVersionsUseCase.execute(),
      ]);
      setState((s) => ({
        ...s,
        privacy: { current, versions, loading: false, error: null },
      }));
    } catch (e) {
      const msg = handleError(e).message;
      setState((s) => ({
        ...s,
        privacy: { ...s.privacy, loading: false, error: msg },
      }));
    }
  }, [getPrivacyPolicyUseCase, getPrivacyPolicyVersionsUseCase]);

  const refreshLegalDocuments = useCallback(async () => {
    await Promise.all([refreshTerms(), refreshPrivacy()]);
  }, [refreshTerms, refreshPrivacy]);

  const fetchLegalSnapshots = useCallback(async (): Promise<LegalDocumentsSnapshot> => {
    const [terms, privacy] = await Promise.all([
      getTermsOfUseUseCase.execute(),
      getPrivacyPolicyUseCase.execute(),
    ]);
    return { terms, privacy };
  }, [getTermsOfUseUseCase, getPrivacyPolicyUseCase]);

  useEffect(() => {
    void refreshLegalDocuments();
  }, [refreshLegalDocuments]);

  const updateTerms = useCallback(
    async (dto: UpdateLegalDocumentDto): Promise<AppContentOperationResult> => {
      setState((s) => ({ ...s, terms: { ...s.terms, loading: true, error: null } }));
      try {
        await updateTermsOfUseUseCase.execute(dto);
        await invalidateAppContentCacheUseCase.execute('termsOfUse');
        await refreshTerms();
        return { success: true, error: null };
      } catch (e) {
        const msg = handleError(e).message;
        setState((s) => ({ ...s, terms: { ...s.terms, loading: false, error: msg } }));
        return { success: false, error: msg };
      }
    },
    [updateTermsOfUseUseCase, invalidateAppContentCacheUseCase, refreshTerms]
  );

  const updatePrivacy = useCallback(
    async (dto: UpdateLegalDocumentDto): Promise<AppContentOperationResult> => {
      setState((s) => ({ ...s, privacy: { ...s.privacy, loading: true, error: null } }));
      try {
        await updatePrivacyPolicyUseCase.execute(dto);
        await invalidateAppContentCacheUseCase.execute('privacyPolicy');
        await refreshPrivacy();
        return { success: true, error: null };
      } catch (e) {
        const msg = handleError(e).message;
        setState((s) => ({ ...s, privacy: { ...s.privacy, loading: false, error: msg } }));
        return { success: false, error: msg };
      }
    },
    [updatePrivacyPolicyUseCase, invalidateAppContentCacheUseCase, refreshPrivacy]
  );

  const clearErrorTerms = useCallback(() => {
    setState((s) => ({ ...s, terms: { ...s.terms, error: null } }));
  }, []);

  const clearErrorPrivacy = useCallback(() => {
    setState((s) => ({ ...s, privacy: { ...s.privacy, error: null } }));
  }, []);

  const events: AppContentEvents = useMemo(
    () => ({
      refreshTerms,
      refreshPrivacy,
      refreshLegalDocuments,
      fetchLegalSnapshots,
      updateTerms,
      updatePrivacy,
      clearErrorTerms,
      clearErrorPrivacy,
    }),
    [
      refreshTerms,
      refreshPrivacy,
      refreshLegalDocuments,
      fetchLegalSnapshots,
      updateTerms,
      updatePrivacy,
      clearErrorTerms,
      clearErrorPrivacy,
    ]
  );

  const value = useMemo(() => ({ state, events }), [state, events]);

  return <AppContentContext.Provider value={value}>{children}</AppContentContext.Provider>;
}

export function useAppContent(): AppContentContextValue {
  const ctx = useContext(AppContentContext);
  if (!ctx) {
    throw new Error('useAppContent must be used within AppContentProvider');
  }
  return ctx;
}
